import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { Stack, Card, Group, Text, Switch, Select, Button, Divider, ActionIcon, Tooltip, Avatar, TextInput, Center } from '@mantine/core';
import PageHeader from '@/components/common/PageHeader';
import { useT } from '@/i18n';
import { activePageAtom, appConfigAtom, homeBackgroundImageAtom, homeTimeImageAtom, themeAtom } from '@/stores/atoms/uiAtoms';
import { languageAtom } from '@/stores/atoms/i18nAtoms';
import { authUserAtom, authModalOpenAtom } from '@/stores/atoms/authAtoms';
import { signOut, deleteCurrentUserAccount } from '@/utils/firebase';
import { logoutAuth, getStoredAuthToken, updateMyAccount } from '@/utils/userApi';
import { clearUserNamespace } from '@/renderer/storage/userLocal';
import { confirmDialog } from '@/utils/confirm';
import { notifications } from '@mantine/notifications';
import { sessionsAtom, recentSessionIdsAtom } from '@/stores/atoms/sessionAtoms';
import { chatsAtom, messagesMapAtom, currentChatIdAtom } from '@/stores/atoms/chatAtoms';
import { mineGamesAtom } from '@/stores/atoms/mineAtoms';
import { httpFetch } from '@/renderer/utils/http';
import { getImageApiBase } from '@/renderer/utils/session';
import { IconLogout, IconCamera, IconCheck, IconPencil, IconChevronRight, IconPalette, IconShield, IconDatabase, IconDiamond } from '@tabler/icons-react';
import { chatUiStyleAtom, chatBackgroundUrlAtom, chatPreferencesAtom, chatAvatarDataUrlAtom, chatNicknameAtom, chatStatusAtom, chatModelUrlAtom, chatModelNameAtom, chatModelApiKeyAtom, waPersonasAtom, waMessagesMapAtom, waCurrentPersonaIdAtom, waInitDoneAtom, waSelfNicknameAtom, waSelfDefineAtom, waSelfMemoryAtom, waMaxMsgCntAtom, waWallpaperMapAtom, waFeedsAtom, } from '@/stores/atoms/whatsappAtoms';
import RechargeOverlay from '@/components/payments/RechargeOverlay';
import { rechargeOverlayOpenAtom } from '@/stores/atoms/walletAtoms';
const SettingsApp = () => {
    const t = useT();
    const setActivePage = useSetAtom(activePageAtom);
    const [config, setConfig] = useAtom(appConfigAtom);
    const [, setSessions] = useAtom(sessionsAtom);
    const [, setRecentIds] = useAtom(recentSessionIdsAtom);
    const [, setChats] = useAtom(chatsAtom);
    const [, setMessagesMap] = useAtom(messagesMapAtom);
    const [, setCurrentChatId] = useAtom(currentChatIdAtom);
    const [, setMineGames] = useAtom(mineGamesAtom);
    const [authUser] = useAtom(authUserAtom);
    const [, setAuthUser] = useAtom(authUserAtom);
    const [, setAuthModalOpen] = useAtom(authModalOpenAtom);
    const [theme, setTheme] = useAtom(themeAtom);
    const [, setHomeBg] = useAtom(homeBackgroundImageAtom);
    const [, setHomeTimeImg] = useAtom(homeTimeImageAtom);
    const [lang, setLang] = useAtom(languageAtom);
    const [, setRechargeOpen] = useAtom(rechargeOverlayOpenAtom);
    // WhatsApp atoms setters
    const [, setWaUiStyle] = useAtom(chatUiStyleAtom);
    const [, setWaBgUrl] = useAtom(chatBackgroundUrlAtom);
    const [, setWaPrefs] = useAtom(chatPreferencesAtom);
    const [, setWaAvatar] = useAtom(chatAvatarDataUrlAtom);
    const [, setWaNick] = useAtom(chatNicknameAtom);
    const [, setWaStatus] = useAtom(chatStatusAtom);
    const [, setWaModelUrl] = useAtom(chatModelUrlAtom);
    const [, setWaModelName] = useAtom(chatModelNameAtom);
    const [, setWaApiKey] = useAtom(chatModelApiKeyAtom);
    const [, setWaPersonas] = useAtom(waPersonasAtom);
    const [, setWaMsgsMap] = useAtom(waMessagesMapAtom);
    const [, setWaCurId] = useAtom(waCurrentPersonaIdAtom);
    const [, setWaInit] = useAtom(waInitDoneAtom);
    const [, setWaSelfNick] = useAtom(waSelfNicknameAtom);
    const [, setWaSelfDefine] = useAtom(waSelfDefineAtom);
    const [, setWaSelfMemory] = useAtom(waSelfMemoryAtom);
    const [, setWaMaxCnt] = useAtom(waMaxMsgCntAtom);
    const [, setWaWallpapers] = useAtom(waWallpaperMapAtom);
    const [, setWaFeeds] = useAtom(waFeedsAtom);
    // Personal info state
    const [displayNameInput, setDisplayNameInput] = React.useState('');
    const [avatar, setAvatar] = React.useState(authUser?.photoURL || undefined);
    const [savingProfile, setSavingProfile] = React.useState(false);
    const fileRef = React.useRef(null);
    const uploadImageDataUrl = async (dataUrl, filename) => {
        const api = getImageApiBase();
        const res = await httpFetch(`${api}/api/images`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ data: dataUrl, filename }) });
        if (!res.ok)
            throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json || json.success === false)
            throw new Error(String(json?.error || 'Upload failed'));
        return json.image_url || json.url;
    };
    const onPickAvatar = async (file) => {
        if (!file)
            return;
        if (!authUser) {
            setAuthModalOpen(true);
            return;
        }
        const token = getStoredAuthToken();
        if (!token) {
            setAuthModalOpen(true);
            notifications.show({ color: 'red', title: t('error.title'), message: '请使用邮箱/Google/Apple 登录后再编辑资料', withCloseButton: true });
            return;
        }
        try {
            const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || ''));
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            setAvatar(dataUrl);
            const url = await uploadImageDataUrl(dataUrl, file.name);
            const updated = await updateMyAccount({ avatar_url: url });
            setAvatar(updated.avatar_url || url);
            setAuthUser({ uid: updated.id, displayName: updated.display_name || undefined, email: updated.email || undefined, photoURL: updated.avatar_url || url });
            notifications.show({ color: 'green', title: t('create.image.uploaded'), message: t('create.image.coverUpdated'), withCloseButton: true });
        }
        catch (e) {
            notifications.show({ color: 'red', title: t('error.title'), message: String(e?.message || '头像更新失败'), withCloseButton: true });
        }
    };
    const saveProfile = async () => {
        if (!authUser) {
            setAuthModalOpen(true);
            return;
        }
        const token = getStoredAuthToken();
        if (!token) {
            setAuthModalOpen(true);
            notifications.show({ color: 'red', title: t('error.title'), message: '请使用邮箱/Google/Apple 登录后再编辑资料', withCloseButton: true });
            return;
        }
        setSavingProfile(true);
        try {
            const updated = await updateMyAccount({ display_name: displayNameInput || null });
            setAuthUser({ uid: updated.id, displayName: updated.display_name || undefined, email: updated.email || undefined, photoURL: updated.avatar_url || undefined });
            notifications.show({ color: 'green', title: '资料已更新', message: '您的个人信息已保存。', withCloseButton: true });
            setDisplayNameInput('');
        }
        catch (e) {
            notifications.show({ color: 'red', title: '更新失败', message: String(e?.message || '保存失败'), withCloseButton: true });
        }
        finally {
            setSavingProfile(false);
        }
    };
    const setTelemetry = (v) => setConfig({ ...config, telemetryEnabled: v });
    const setRetention = (days) => setConfig({ ...config, dataRetentionDays: days });
    const deleteAllLocalData = async () => {
        const ok = await confirmDialog({ title: t('settings.deleteData'), message: t('settings.deleteDataConfirm'), labels: { confirm: t('confirm.ok'), cancel: t('confirm.cancel') } });
        if (!ok)
            return;
        try {
            if (!authUser) {
                setAuthModalOpen(true);
                return;
            }
            clearUserNamespace(authUser.uid);
            try {
                await logoutAuth();
            }
            catch { }
            try {
                await signOut();
            }
            catch { }
            try {
                localStorage.removeItem('firebase_uid');
            }
            catch { }
            try {
                setAuthUser(null);
            }
            catch { }
            setSessions([]);
            setRecentIds([]);
            setChats([]);
            setMessagesMap({});
            setCurrentChatId(null);
            setMineGames([]);
            // Clear WhatsApp local data
            setWaUiStyle('default');
            setWaBgUrl('');
            setWaPrefs({ readReceipts: true, vibrate: true });
            setWaAvatar(undefined);
            setWaNick('user');
            setWaStatus('在线');
            setWaModelUrl('http://localhost:11434/v1');
            setWaModelName('qwen3-7b');
            setWaApiKey('');
            setWaPersonas([]);
            setWaMsgsMap({});
            setWaCurId(null);
            setWaInit(false);
            setWaSelfNick('user');
            setWaSelfDefine('');
            setWaSelfMemory('');
            setWaMaxCnt(7);
            setWaWallpapers({});
            setWaFeeds([]);
            notifications.show({ color: 'green', title: t('settings.deleted'), message: t('settings.deletedMsg'), withCloseButton: true });
        }
        catch (e) {
            notifications.show({ color: 'red', title: t('settings.deleteData'), message: String(e?.message || 'Error'), withCloseButton: true });
        }
    };
    const deleteAccount = async () => {
        const ok = await confirmDialog({ title: t('settings.deleteAccount'), message: t('settings.deleteAccountConfirm'), labels: { confirm: t('confirm.ok'), cancel: t('confirm.cancel') } });
        if (!ok)
            return;
        try {
            if (!authUser) {
                setAuthModalOpen(true);
                return;
            }
            const uid = authUser.uid;
            await deleteCurrentUserAccount();
            clearUserNamespace(uid);
            setSessions([]);
            setRecentIds([]);
            setChats([]);
            setMessagesMap({});
            setCurrentChatId(null);
            setMineGames([]);
            try {
                await logoutAuth();
            }
            catch { }
            try {
                await signOut();
            }
            catch { }
            try {
                localStorage.removeItem('firebase_uid');
            }
            catch { }
            try {
                setAuthUser(null);
            }
            catch { }
            // Clear WhatsApp local data
            setWaUiStyle('default');
            setWaBgUrl('');
            setWaPrefs({ readReceipts: true, vibrate: true });
            setWaAvatar(undefined);
            setWaNick('user');
            setWaStatus('在线');
            setWaModelUrl('http://localhost:11434/v1');
            setWaModelName('qwen3-7b');
            setWaApiKey('');
            setWaPersonas([]);
            setWaMsgsMap({});
            setWaCurId(null);
            setWaInit(false);
            setWaSelfNick('user');
            setWaSelfDefine('');
            setWaSelfMemory('');
            setWaMaxCnt(7);
            setWaWallpapers({});
            setWaFeeds([]);
            notifications.show({ color: 'green', title: t('settings.deleted'), message: t('settings.deletedMsg'), withCloseButton: true });
            setActivePage('home');
        }
        catch (e) {
            const msg = e?.code === 'auth/requires-recent-login' ? t('settings.reauthRequired') : (e?.message || 'Error');
            notifications.show({ color: 'red', title: t('error.title'), message: String(msg), withCloseButton: true });
        }
    };
    const rightExtra = undefined;
    const [section, setSection] = React.useState(null);
    if (!authUser) {
        return (_jsx(Center, { style: { minHeight: 'calc(100dvh - var(--footer-bar-height, 64px))' }, children: _jsxs(Stack, { p: "lg", gap: "md", align: "center", justify: "center", children: [_jsx(Text, { size: "lg", style: { color: 'var(--app-text-primary)' }, children: t('profile.loginRequired') }), _jsx(Button, { onClick: () => setAuthModalOpen(true), children: t('auth.login') })] }) }));
    }
    const MenuItem = ({ icon, title, description, onClick }) => (_jsx(Card, { withBorder: true, radius: "md", p: "sm", className: "card-soft", onClick: onClick, style: { cursor: 'pointer' }, children: _jsxs(Group, { justify: "space-between", align: "center", children: [_jsxs(Group, { gap: 10, align: "center", children: [icon, _jsxs("div", { children: [_jsx(Text, { fw: 600, size: "sm", style: { color: 'var(--app-text-primary)' }, children: title }), description && _jsx(Text, { size: "xs", style: { color: 'var(--app-text-secondary)' }, children: description })] })] }), _jsx(IconChevronRight, { size: 16 })] }) }));
    const renderPersonalCard = () => (_jsx(Card, { withBorder: true, radius: "md", p: "sm", className: "card-soft", children: _jsxs(Stack, { gap: "sm", align: "center", children: [_jsxs("div", { style: { position: 'relative', display: 'inline-block' }, children: [_jsx(Tooltip, { label: t('profile.uploadAvatar'), children: _jsx(Avatar, { size: 96, radius: "xl", src: avatar, onClick: () => fileRef.current?.click(), style: { cursor: 'pointer' }, children: (t('profile.user').charAt(0)).toUpperCase() }) }), _jsx(Tooltip, { label: t('profile.uploadAvatar'), children: _jsx("div", { role: "button", "aria-label": t('profile.uploadAvatar'), onClick: () => fileRef.current?.click(), style: { position: 'absolute', right: 2, bottom: 2, width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)', cursor: 'pointer' }, children: _jsx(IconCamera, { size: 14 }) }) })] }), _jsx("input", { ref: fileRef, type: "file", accept: "image/*", style: { display: 'none' }, onChange: (e) => onPickAvatar(e.currentTarget.files?.[0] || null) }), _jsxs(Group, { gap: 6, justify: "center", align: "center", wrap: "nowrap", children: [_jsx(Text, { fw: 700, size: "lg", style: { textAlign: 'center', color: 'var(--app-text-primary)' }, children: authUser?.displayName || t('profile.user') }), _jsx(ActionIcon, { size: "sm", variant: "light", "aria-label": t('edit'), onClick: () => {
                                const token = getStoredAuthToken();
                                if (!token) {
                                    setAuthModalOpen(true);
                                    notifications.show({ color: 'red', title: t('error.title'), message: '请使用邮箱/Google/Apple 登录后再编辑资料', withCloseButton: true });
                                    return;
                                }
                                setDisplayNameInput(authUser?.displayName || '');
                            }, children: _jsx(IconPencil, { size: 14 }) })] }), displayNameInput !== '' && (_jsxs(Group, { gap: 6, justify: "center", align: "center", wrap: "nowrap", children: [_jsx(TextInput, { value: displayNameInput, onChange: (e) => setDisplayNameInput(e.currentTarget.value), size: "xs", placeholder: t('profile.user'), style: { minWidth: 180 } }), _jsx(ActionIcon, { size: "sm", variant: "light", "aria-label": t('confirm.ok'), onClick: saveProfile, disabled: savingProfile, children: _jsx(IconCheck, { size: 14 }) })] }))] }) }));
    const renderThemeSection = () => (_jsx(Card, { withBorder: true, radius: "md", p: "md", className: "card-soft", children: _jsxs(Stack, { gap: "sm", children: [_jsxs(Group, { align: "center", justify: "space-between", children: [_jsx(Text, { size: "sm", style: { color: 'var(--app-text-primary)' }, children: "\u4E3B\u9898" }), _jsx(Select, { data: [{ value: 'auto', label: '跟随系统' }, { value: 'light', label: '浅色' }, { value: 'dark', label: '深色' }], value: theme, onChange: (v) => setTheme(v || 'auto'), w: 160 })] }), _jsx(Divider, { my: "xs" }), _jsxs(Group, { align: "center", justify: "space-between", children: [_jsxs("div", { children: [_jsx(Text, { size: "sm", style: { color: 'var(--app-text-primary)' }, children: "\u9996\u9875\u80CC\u666F\u56FE" }), _jsx(Text, { size: "xs", style: { color: 'var(--app-text-secondary)' }, children: "\u652F\u6301\u672C\u5730\u56FE\u7247\uFF0C\u5EFA\u8BAE\u5C3A\u5BF8\u63A5\u8FD1\u5C4F\u5E55\u6BD4\u4F8B" })] }), _jsxs(Group, { gap: 8, children: [_jsx("input", { type: "file", accept: "image/*", onChange: async (e) => {
                                        const file = e.currentTarget.files?.[0];
                                        if (!file)
                                            return;
                                        try {
                                            const dataUrl = await new Promise((resolve, reject) => {
                                                const reader = new FileReader();
                                                reader.onload = () => resolve(String(reader.result || ''));
                                                reader.onerror = reject;
                                                reader.readAsDataURL(file);
                                            });
                                            const url = await uploadImageDataUrl(dataUrl, file.name);
                                            setHomeBg(url);
                                            notifications.show({ color: 'green', title: '已更新', message: '首页背景图已更改', withCloseButton: true });
                                        }
                                        catch (err) {
                                            notifications.show({ color: 'red', title: '更新失败', message: String(err?.message || '上传失败'), withCloseButton: true });
                                        }
                                    } }), _jsx(Button, { variant: "light", onClick: () => setHomeBg(null), children: "\u6062\u590D\u9ED8\u8BA4" })] })] }), _jsx(Divider, { my: "xs" }), _jsxs(Group, { align: "center", justify: "space-between", children: [_jsxs("div", { children: [_jsx(Text, { size: "sm", style: { color: 'var(--app-text-primary)' }, children: "\u65F6\u95F4\u533A\u57DF\u56FE\u7247" }), _jsx(Text, { size: "xs", style: { color: 'var(--app-text-secondary)' }, children: "\u7528\u4E8E\u9996\u9875\u65F6\u95F4\u533A\u57DF\u7684\u80CC\u666F\uFF0C\u5EFA\u8BAE\u7EAF\u8272\u6216\u7B80\u6D01\u56FE\u7247" })] }), _jsxs(Group, { gap: 8, children: [_jsx("input", { type: "file", accept: "image/*", onChange: async (e) => {
                                        const file = e.currentTarget.files?.[0];
                                        if (!file)
                                            return;
                                        try {
                                            const dataUrl = await new Promise((resolve, reject) => {
                                                const reader = new FileReader();
                                                reader.onload = () => resolve(String(reader.result || ''));
                                                reader.onerror = reject;
                                                reader.readAsDataURL(file);
                                            });
                                            const url = await uploadImageDataUrl(dataUrl, file.name);
                                            setHomeTimeImg(url);
                                            notifications.show({ color: 'green', title: '已更新', message: '时间区域图片已更改', withCloseButton: true });
                                        }
                                        catch (err) {
                                            notifications.show({ color: 'red', title: '更新失败', message: String(err?.message || '上传失败'), withCloseButton: true });
                                        }
                                    } }), _jsx(Button, { variant: "light", onClick: () => setHomeTimeImg(null), children: "\u6062\u590D\u9ED8\u8BA4" })] })] })] }) }));
    const renderLanguageSection = () => (_jsx(Card, { withBorder: true, radius: "md", p: "md", className: "card-soft", children: _jsx(Stack, { gap: "sm", children: _jsxs(Group, { align: "center", justify: "space-between", children: [_jsx(Text, { size: "sm", style: { color: 'var(--app-text-primary)' }, children: t('language') }), _jsx(Select, { data: [{ value: 'zh-CN', label: t('language.zh-CN') }, { value: 'en-US', label: t('language.en-US') }], value: lang, onChange: (v) => setLang(v || 'zh-CN'), w: 160 })] }) }) }));
    // 合并至主题菜单后，单独的首页背景/时间区设置移除
    const renderPrivacySection = () => (_jsx(Card, { withBorder: true, radius: "md", p: "md", className: "card-soft", children: _jsxs(Stack, { gap: "sm", children: [_jsx(Text, { fw: 700, style: { color: 'var(--app-text-primary)' }, children: t('settings.privacy') }), _jsxs(Group, { justify: "space-between", children: [_jsx(Text, { size: "sm", style: { color: 'var(--app-text-primary)' }, children: t('settings.telemetry') }), _jsx(Switch, { checked: Boolean(config.telemetryEnabled), onChange: (e) => setTelemetry(e.currentTarget.checked) })] }), _jsx(Group, { children: _jsx(Button, { variant: "light", onClick: () => { try {
                            window.open('https://mallorb.com/privacy', '_blank');
                        }
                        catch { } }, children: t('settings.privacyPolicy') }) })] }) }));
    const renderDataSection = () => (_jsx(Card, { withBorder: true, radius: "md", p: "md", className: "card-soft", children: _jsxs(Stack, { gap: "sm", children: [_jsx(Text, { fw: 700, style: { color: 'var(--app-text-primary)' }, children: t('settings.dataControl') }), _jsx(Text, { size: "sm", style: { color: 'var(--app-text-secondary)' }, children: t('settings.dataControlDesc') }), _jsxs(Group, { justify: "space-between", children: [_jsx(Text, { size: "sm", style: { color: 'var(--app-text-primary)' }, children: t('settings.dataRetention') }), _jsx(Select, { data: [{ value: '0', label: t('settings.retention.never') }, { value: '30', label: '30 days' }, { value: '90', label: '90 days' }, { value: '365', label: '1 year' }], value: config?.dataRetentionDays != null ? String(config.dataRetentionDays) : '0', onChange: (v) => setRetention(v ? Number(v) : null), w: 160 })] }), _jsxs(Group, { children: [_jsx(Button, { color: "red", variant: "light", onClick: deleteAllLocalData, children: t('settings.deleteData') }), _jsx(Button, { color: "red", onClick: deleteAccount, children: t('settings.deleteAccount') })] }), _jsx(Divider, { my: "xs" }), _jsx(Text, { size: "xs", style: { color: 'var(--app-text-secondary)' }, children: t('settings.usCompliance') })] }) }));
    const renderMenu = () => (_jsxs(Stack, { gap: "sm", children: [renderPersonalCard(), _jsx(MenuItem, { icon: _jsx(IconPalette, { size: 18 }), title: "\u4E3B\u9898", description: "\u4E3B\u9898/\u9996\u9875\u80CC\u666F/\u65F6\u95F4\u533A\u56FE\u7247", onClick: () => setSection('theme') }), _jsx(MenuItem, { icon: _jsx(IconShield, { size: 18 }), title: t('settings.privacy'), onClick: () => setSection('privacy') }), _jsx(MenuItem, { icon: _jsx(IconDatabase, { size: 18 }), title: t('settings.dataControl'), onClick: () => setSection('data') }), _jsx(MenuItem, { icon: _jsx(IconDiamond, { size: 18 }), title: "\u5145\u503C\u4F1A\u5458", description: "Stripe / Apple / Google", onClick: () => setRechargeOpen(true) }), _jsx(MenuItem, { icon: _jsx(IconLogout, { size: 18 }), title: t('profile.signOut'), onClick: async () => {
                    try {
                        await logoutAuth();
                    }
                    catch { }
                    try {
                        await signOut();
                    }
                    catch { }
                    try {
                        localStorage.removeItem('firebase_uid');
                    }
                    catch { }
                    try {
                        setAuthUser(null);
                    }
                    catch { }
                    setActivePage('home');
                } })] }));
    const renderSection = () => {
        switch (section) {
            case 'language': return renderLanguageSection();
            case 'theme': return renderThemeSection();
            case 'privacy': return renderPrivacySection();
            case 'data': return renderDataSection();
            default: return null;
        }
    };
    return (_jsxs(Stack, { p: "sm", gap: "sm", style: { height: '100%', overflow: 'hidden' }, children: [_jsx(PageHeader, { title: section ? '设置' : t('settings.title'), rightExtra: rightExtra, onBack: () => { if (section)
                    setSection(null);
                else
                    setActivePage('home'); } }), !section ? renderMenu() : renderSection(), _jsx(RechargeOverlay, {})] }));
};
export default SettingsApp;
